// The jobs about one activation, as siblings in the rail.
//
// NOTHING HERE READS ANYTHING, AND THAT IS THE PROPERTY WORTH STATING. A tab
// strip is the obvious place to prefetch: four reports, one activation, the
// person is going to look at them anyway. Every one of those reads writes a row
// in `report_access` naming the activation and the person who read it — that
// table exists so a client can one day ask who at Nova looked at their attendee
// list — so prefetching would file three disclosures nobody made against the
// name of somebody who opened one screen. The export is worse again: it is the
// read that hands over personal data, and it is behind a purpose somebody has to
// choose. So these are links, each surface reads when it is opened, and the
// export reads only when it is asked to.
//
// THE NAMES ARE THE READER'S RATHER THAN THE ENDPOINT'S, and they match how
// XP Fixtures is sold: Dashboard, then the show's own record, then
// Registration, Kiosk, Data, People. Show details is a write, not a report — it
// lives here because the hall, the clock and the run have to be findable
// without already being on the kiosk page that reads them. The kiosk page still
// shows those facts and still sends here to change them. The keys behind
// Dashboard and Data are still REPORT_ENDPOINTS's own, so a fifth report added
// to the service is a compile error in App.tsx rather than a screen nobody can
// reach. Reconciliation, opt-in and the survey stay on their old urls and are
// not siblings of Dashboard: opening the Dashboard is asking for the day, and
// those three questions live under it. They hang as a nested list when the
// section is open, the way Registration's pages hang under Registration,
// because a screen with no mark on the rail is a screen somebody cannot leave.
//
// PEOPLE IS THE ONE ITEM HERE THAT READS PERSONAL DATA A NAME AT A TIME, and it
// is a rail item rather than a panel of Data for the reason
// docs/console-people-activity.md gives: producing a file of an activation's
// attendees and reading one attendee's record are different disclosures with
// different records behind them, and burying the second inside the first would
// make it look like a step of the export.

import type { ReactNode } from "react";
import type { ReportName } from "../api/endpoints.js";
import type { BoardPanel, KioskPanel, RegistrationPanel, RidePanel, WheelPanel } from "../routes/route.js";
import { BoardNav } from "./BoardNav.js";
import { DashboardNav, dashboardPage, type DashboardPage } from "./DashboardNav.js";
import { KioskNav } from "./KioskNav.js";
import { RegistrationNav } from "./RegistrationNav.js";
import { isRideToSurviveActivation } from "../../../api/src/auth/otpTemplates.js";
import { RideNav } from "./RideNav.js";
import { WheelNav } from "./WheelNav.js";
import { offers, type Reader } from "./roleAccess.js";

export type ShowHere =
  | ReportName
  | "kiosk"
  | "tablets"
  | "registration"
  | "details"
  | "ride"
  | "people"
  | "unverified"
  | "draw"
  | "wheel"
  | "board"
  | "themes"
  | "activity";

export type ReportNavProps = {
  readonly here: ShowHere;
  readonly onDashboard: (page?: DashboardPage) => void;
  readonly onDetails: () => void;
  readonly onRide: (panel?: RidePanel) => void;
  readonly ridePanel: RidePanel | null;
  readonly activationId: string | null;
  readonly onRegistration: (panel?: RegistrationPanel) => void;
  readonly registrationPanel: RegistrationPanel | null;
  readonly onKiosk: (panel?: KioskPanel) => void;
  readonly kioskPanel: KioskPanel | null;
  readonly onData: () => void;
  readonly onPeople: () => void;
  readonly onUnverified: () => void;
  readonly onDraw: () => void;
  readonly onWheel: (panel?: WheelPanel) => void;
  readonly wheelPanel: WheelPanel | null;
  readonly onBoard: (panel?: BoardPanel) => void;
  readonly boardPanel: BoardPanel | null;
  readonly onActivity: () => void;
  readonly onThemes: () => void;
  /**
   * Whose rail this is, as the sign-in said: the role and the export switch.
   * roleAccess.ts decides from it what is offered, and App.tsx decides from
   * the same map what a route opens, so the two cannot disagree.
   */
  readonly reader: Reader;
  /** The activation's own name, or null while it is still being read. */
  readonly showTitle: string | null;
  readonly showUnnamed: boolean;
};

export function ReportNav({
  here,
  onDashboard,
  onDetails,
  onRide,
  ridePanel,
  activationId,
  onRegistration,
  registrationPanel,
  onKiosk,
  kioskPanel,
  onData,
  onPeople,
  onUnverified,
  onDraw,
  onWheel,
  wheelPanel,
  onBoard,
  boardPanel,
  onActivity,
  onThemes,
  reader,
  showTitle,
  showUnnamed,
}: ReportNavProps): ReactNode {
  // EVERY ITEM FROM THE ONE MAP (roleAccess.ts), which check.mjs ties to the
  // service's guards. Named one per item so a mutation that ignores the map has
  // one line to break. Dashboard and Event activity have no line because every
  // role reads them; the map still says so, and the router still asks it.
  const offerDetails = offers(reader, "details");
  const offerThemes = offers(reader, "themes");
  const offerData = offers(reader, "data");
  const offerPeople = offers(reader, "people");
  const offerUnverified = offers(reader, "unverified");
  const offerDraw = offers(reader, "draw");
  const offerRegistration = offers(reader, "registration");
  // The wheel's Setup and Prizes are Nova's; its Results are whoever reads
  // People's, so a Viewer is offered Wheel and it opens on Results.
  const offerWheelSetup = offers(reader, "wheel");
  const offerWheelResults = offers(reader, "wheelResults");
  const offerWheel = offerWheelSetup || offerWheelResults;
  const offerKiosk = offers(reader, "kiosk");
  const offerRide = offers(reader, "game");
  // The board's Setup is every role's, a Viewer's read only; its Theme is Nova's.
  const offerBoard = offers(reader, "board");
  const onKioskHere = here === "kiosk" || here === "tablets";
  const dashPage = dashboardPage(here);
  const onDashboardHere = dashPage !== null;

  // BUTTONS RATHER THAN LIST ITEMS, which is a change the shared rail brought
  // and not a loss. The strip was a <ul> so that a screen reader announced "list
  // of six"; the shared .nav is a <nav> with an accessible name and buttons
  // inside it, which announces the name instead — "This show, navigation" — and
  // that is the more useful of the two sentences here, because the count of
  // items varies with the reader's role and the name does not.
  return (
    <>
      <div className="rail-sec">This show</div>
      {showTitle === null ? null : (
        <p className={showUnnamed ? "rail-show unnamed" : "rail-show"}>{showTitle}</p>
      )}
      <nav className="nav" aria-label="This show">
        <button
          type="button"
          className={onDashboardHere ? "open" : undefined}
          onClick={() => onDashboard()}
        >
          Dashboard
        </button>
        {onDashboardHere && dashPage !== null ? (
          <DashboardNav page={dashPage} onOpen={onDashboard} />
        ) : null}
        {offerDetails ? (
          <button
            type="button"
            aria-current={here === "details" ? "page" : undefined}
            onClick={onDetails}
          >
            Show details
          </button>
        ) : null}
        {/* AFTER SHOW DETAILS, AS THE HAND-OFF DRAWS IT. What the show wears is
            the show's own record, next to its name and hall, and it is above
            the apps because every app below reads it. */}
        {offerThemes ? (
          <button
            type="button"
            aria-current={here === "themes" ? "page" : undefined}
            onClick={onThemes}
          >
            Themes
          </button>
        ) : null}
        {offerData ? (
          <button
            type="button"
            aria-current={here === "export" ? "page" : undefined}
            onClick={onData}
          >
            Data
          </button>
        ) : null}
        {/* NEXT TO DATA RATHER THAN NEXT TO DASHBOARD, and the adjacency is the
            argument. Data produces a file of this activation's people; People is
            the same rows read one at a time, with a record for each. Somebody
            answering a question about one person should find the two together,
            because the answer to "which files was this person in" lives on
            neither of them and both say so. */}
        {offerPeople ? (
          <button
            type="button"
            // `aria-current` and no `open` class, unlike Kiosk and Ride. One
            // person's record is a segment under this route rather than a panel
            // beside it, so there is no nested list to hang open, and the rail
            // says People whether the reader is on the list or on a record.
            aria-current={here === "people" ? "page" : undefined}
            onClick={onPeople}
          >
            People
          </button>
        ) : null}
        {/* UNDER PEOPLE RATHER THAN UNDER DATA, and the adjacency is the
            argument again. These are the two lists of what happened at a stand
            and they do not overlap: a row leaves this one and appears in that
            one the moment a mailbox is proved. Somebody who has just looked at
            an activation's registrations and is wondering how many did not
            finish is one item away from the answer.

            NOT A SUB-ITEM OF PEOPLE, THOUGH IT SITS UNDER IT. The nested lists
            on this rail hang off a route with panels, and there are none here.
            A `.sub` under People would also say this list is part of that one,
            which is the claim the route file refuses at length: nesting states
            a containment, and these two sets are disjoint. */}
        {offerUnverified ? (
          <button
            type="button"
            aria-current={here === "unverified" ? "page" : undefined}
            onClick={onUnverified}
          >
            Unverified emails
          </button>
        ) : null}
        {/* AFTER THE TWO REGISTRATION LISTS, NOT UNDER GAME. The pool is
            completed registrations at this activation, on any show that is
            running a chance-to-win, not a Ride scoreboard. Hanging it off Game
            would hide a company-wide tool behind one arcade. */}
        {offerDraw ? (
          <button
            type="button"
            aria-current={here === "draw" ? "page" : undefined}
            onClick={onDraw}
          >
            Draw
          </button>
        ) : null}
        {/* NO `offer` LINE FOR THIS ONE, because roleAccess.ts opens it for
            every role. The feed answers a client viewer a real page: migration
            131 grants it to both reporting roles and withholds the attribution
            from one of them by privilege rather than by refusing the read.

            LAST OF THIS SHOW'S PAGES, AND LAST FOR EVERY ROLE, WHICH IS WHY IT
            IS NOT BESIDE DASHBOARD. Every other item here is one job at this activation, and
            the two adjacency arguments above are about pairs of jobs. This one
            is the record of all of them, including the reads: producing a file
            writes a row on it and so does opening this page. An item about
            everything above it belongs under everything above it, and being the
            end of the list rather than a position in the middle means it does
            not move when the reader's role changes what is drawn.

            "Activity log" AND NOT "Activity". RideNav labels its second panel
            Activity, and that panel is on the rail whenever somebody is on the
            Game desk, so the shorter name would put the same word on this strip
            twice. route.ts carries the argument, including why the url keeps the
            service's own word regardless. */}
        <button
          type="button"
          aria-current={here === "activity" ? "page" : undefined}
          onClick={onActivity}
        >
          Event activity
        </button>
      </nav>

      {/* THE APPS, IN A GROUP OF THEIR OWN, as the design draws the rail (TASKS
          §143, step 6). Above are this show's own pages, the reads and the
          records; here are the things the show runs on a stand, each with its
          pages hanging open under it while it is the one in use. */}
      {/* No group at all for a reader offered no app, rather than a heading
          over nothing: a Viewer's rail since decision C1. */}
      {offerRegistration || offerWheel || offerKiosk || offerRide || offerBoard ? (
      <>
      <div className="rail-sec">Apps</div>
      <nav className="nav" aria-label="Apps">
        {offerRegistration ? (
          <button
            type="button"
            className={here === "registration" ? "open" : undefined}
            onClick={() => onRegistration("open")}
          >
            Registration
          </button>
        ) : null}
        {offerRegistration && here === "registration" && registrationPanel !== null ? (
          <RegistrationNav
            panel={registrationPanel}
            onOpen={onRegistration}
            offerTheme={offers(reader, "registrationTheme")}
            offerEmails={offers(reader, "registrationEmails")}
          />
        ) : null}
        {offerWheel ? (
          <button
            type="button"
            className={here === "wheel" ? "open" : undefined}
            onClick={() => onWheel(offerWheelSetup ? "setup" : "results")}
          >
            Wheel
          </button>
        ) : null}
        {offerWheel && here === "wheel" && wheelPanel !== null ? (
          <WheelNav
            panel={wheelPanel}
            onOpen={onWheel}
            className="sub"
            offer={{ setup: offerWheelSetup, prizes: offers(reader, "wheelPrizes"), results: offerWheelResults }}
          />
        ) : null}
        {offerKiosk ? (
          <button
            type="button"
            className={onKioskHere ? "open" : undefined}
            aria-current={onKioskHere && kioskPanel === null ? "page" : undefined}
            onClick={() => onKiosk()}
          >
            Kiosk
          </button>
        ) : null}
        {offerKiosk && onKioskHere && kioskPanel !== null ? (
          <KioskNav
            panel={kioskPanel}
            onOpen={onKiosk}
            className="sub"
            offer={{
              screens: offers(reader, "kioskScreens"),
              devices: offers(reader, "kioskDevices"),
              theme: offers(reader, "kioskTheme"),
            }}
          />
        ) : null}
        {offerRide ? (
          <button
            type="button"
            className={here === "ride" ? "open" : undefined}
            aria-current={here === "ride" && ridePanel === null ? "page" : undefined}
            onClick={() => onRide()}
          >
            Game
          </button>
        ) : null}
        {offerRide && here === "ride" && ridePanel !== null ? (
          <RideNav
            panel={ridePanel}
            onOpen={onRide}
            className="sub"
            offerSponsor={
              activationId !== null && isRideToSurviveActivation(activationId.toLowerCase()) && offers(reader, "gameSponsor")
            }
            offerMail={activationId !== null && isRideToSurviveActivation(activationId.toLowerCase())}
          />
        ) : null}
        {/* AFTER GAME, AS THE HAND-OFF DRAWS IT: the board is what the game
            feeds. Its Setup is the one record the kiosk's leaderboard piece
            reads too (157, decision C11). */}
        {offerBoard ? (
          <button
            type="button"
            className={here === "board" ? "open" : undefined}
            onClick={() => onBoard("setup")}
          >
            Board
          </button>
        ) : null}
        {offerBoard && here === "board" && boardPanel !== null ? (
          <BoardNav
            panel={boardPanel}
            onOpen={onBoard}
            className="sub"
            offer={{ setup: offerBoard, theme: offers(reader, "boardTheme") }}
          />
        ) : null}
      </nav>
      </>
      ) : null}
    </>
  );
}
